import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import {
  PenNib, CheckCircle, XCircle, LockSimple, Plus, Trash, ArrowsClockwise, UploadSimple, Clock,
} from "@phosphor-icons/react";

const ROLES = ["admin", "analyst", "reviewer", "auditor"];

const fileToDataUrl = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

const StepStatus = ({ status }) => {
  const map = {
    signed: ["bg-green-50 text-green-700 border-green-300", CheckCircle, "Signed"],
    declined: ["bg-red-50 text-red-700 border-red-300", XCircle, "Declined"],
    pending: ["bg-amber-50 text-amber-700 border-amber-300", Clock, "Pending"],
  };
  const [cls, Icon, label] = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[11px] font-semibold uppercase ${cls}`}>
      <Icon size={12} weight="fill" />{label}
    </span>
  );
};

export default function ApprovalWorkflow({
  doc, onChange, basePath = "/documents", entityId, manageRoles = ["admin", "reviewer"],
  entityLabel = "document", bumpsVersion = true,
}) {
  const { user, isRole } = useAuth();
  const eid = entityId || doc.id;
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState("sequential");
  const [autoRestart, setAutoRestart] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [draftSteps, setDraftSteps] = useState([]);
  const [signStep, setSignStep] = useState(null);
  const [signForm, setSignForm] = useState({ typed_name: "", image: null, comment: "", save_to_profile: false });
  const [declineStep, setDeclineStep] = useState(null);
  const [declineComment, setDeclineComment] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/approval-route-templates", { __silent402: true }).then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const canManage = isRole(...manageRoles) || doc.created_by === user?.id;
  const steps = useMemo(() => (doc.approval_steps || []).slice().sort((a, b) => a.order - b.order), [doc.approval_steps]);
  const locked = !!doc.locked;
  const userName = (id) => users.find((u) => u.id === id)?.name || "—";

  const canSign = (step) => {
    if (locked || step.status !== "pending") return false;
    const mine = step.assignee_type === "user" ? step.assignee_user_id === user?.id : user?.role === step.assignee_role;
    if (!mine) return false;
    if ((doc.approval_mode || "sequential") === "sequential") {
      const earlier = steps.filter((s) => s.order < step.order && s.required !== false);
      if (earlier.some((s) => s.status !== "signed")) return false;
    }
    return true;
  };

  // ---- route editor ----
  const startEdit = () => {
    const init = steps.map((s) => ({
      assignee_type: s.assignee_type, assignee_role: s.assignee_role || "reviewer",
      assignee_user_id: s.assignee_user_id || "", label: s.label || "", required: s.required !== false,
    }));
    setMode(doc.approval_mode || "sequential");
    setAutoRestart(!!doc.auto_restart);
    setDraftSteps(init.length ? init : [{ assignee_type: "role", assignee_role: "reviewer", assignee_user_id: "", label: "", required: true }]);
    setEditing(true);
  };
  const addStep = () => setDraftSteps([...draftSteps, { assignee_type: "user", assignee_role: "reviewer", assignee_user_id: "", label: "", required: true }]);
  const setStep = (i, patch) => setDraftSteps(draftSteps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const removeStep = (i) => setDraftSteps(draftSteps.filter((_, idx) => idx !== i));
  const moveStep = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= draftSteps.length) return;
    const next = draftSteps.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setDraftSteps(next);
  };
  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setMode(t.mode || "sequential");
    setDraftSteps((t.steps || []).map((s) => ({
      assignee_type: s.assignee_type, assignee_role: s.assignee_role || "reviewer",
      assignee_user_id: s.assignee_user_id || "", label: s.label || "", required: s.required !== false,
    })));
    toast.success(`Loaded "${t.name}"`);
  };

  const saveRoute = async () => {
    const payload = draftSteps.map((s) => ({
      assignee_type: s.assignee_type,
      assignee_role: s.assignee_type === "role" ? s.assignee_role : null,
      assignee_user_id: s.assignee_type === "user" ? s.assignee_user_id : null,
      label: s.label, required: s.required,
    }));
    if (payload.some((s) => s.assignee_type === "user" && !s.assignee_user_id))
      return toast.error("Pick a person for each named-signer step.");
    setBusy(true);
    try {
      const { data } = await api.put(`${basePath}/${eid}/approval-route`, { mode, steps: payload, auto_restart: autoRestart });
      onChange(data); setEditing(false); toast.success("Approvers saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // ---- signing ----
  const openSign = (step) => {
    setSignStep(step);
    setSignForm({ typed_name: user?.signature_name || user?.name || "", image: user?.signature_image || null, comment: "", save_to_profile: false });
  };
  const submitSign = async () => {
    if (!signForm.typed_name.trim()) return toast.error("Type your name to sign.");
    setBusy(true);
    try {
      const { data } = await api.post(`${basePath}/${eid}/sign`, {
        step_id: signStep.id, typed_name: signForm.typed_name, image: signForm.image,
        use_saved_image: true, save_to_profile: signForm.save_to_profile, comment: signForm.comment,
      });
      onChange(data); setSignStep(null);
      toast.success(data.locked ? `Signed — ${entityLabel} fully approved & locked` : "Signed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitDecline = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${basePath}/${eid}/decline`, { step_id: declineStep?.id, comment: declineComment });
      onChange(data); setDeclineStep(null); setDeclineComment(""); toast.success("Rejected — sent back to the author");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const reopen = async () => {
    if (!window.confirm(`Reopen this approved ${entityLabel} for revision? This clears all signatures${bumpsVersion ? " and bumps the version" : ""}.`)) return;
    setBusy(true);
    try { const { data } = await api.post(`${basePath}/${eid}/reopen`); onChange(data); toast.success("Reopened for revision"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const restart = async () => {
    setBusy(true);
    try { const { data } = await api.post(`${basePath}/${eid}/restart-approval`); onChange(data); toast.success("Approval restarted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onPickImage = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) return toast.error("Image too large (max 1.5 MB).");
    const url = await fileToDataUrl(file);
    target(url);
  };

  return (
    <div className="border border-border bg-card" data-testid="approval-workflow">
      <div className="border-b border-border px-4 py-3 flex items-center justify-between">
        <span className="font-display font-bold flex items-center gap-2"><PenNib size={18} weight="fill" />Approvals & Signatures</span>
        {locked && <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase text-green-700"><LockSimple size={12} weight="fill" />Locked</span>}
      </div>

      <div className="p-4 space-y-3">
        {locked && (
          <div data-testid="locked-banner" className="border border-green-300 bg-green-50 px-3 py-2 text-xs text-green-800">
            This {entityLabel} is fully approved and frozen. Editing is disabled to preserve the signed record.
          </div>
        )}
        {doc.workflow_status === "changes_requested" && !locked && (
          <div data-testid="changes-banner" className="border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">
            Changes were requested. Revise the {entityLabel}, then restart approvals.
          </div>
        )}

        {steps.length === 0 && !editing && (
          <p className="text-sm text-muted-foreground" data-testid="no-approvers">
            No approvers defined yet.{canManage ? ` Add a signer chain to route this ${entityLabel} for sign-off.` : ""}
          </p>
        )}

        {/* Steps list */}
        {!editing && steps.map((s, i) => (
          <div key={s.id} data-testid={`step-${i}`} className="border border-border">
            <div className="flex items-center justify-between px-3 py-2 bg-secondary/40">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-mono text-xs text-muted-foreground">{i + 1}</span>
                <span className="text-sm font-semibold truncate">
                  {s.label || (s.assignee_type === "role" ? `${s.assignee_role} (role)` : userName(s.assignee_user_id))}
                </span>
              </div>
              <StepStatus status={s.status} />
            </div>
            <div className="px-3 py-2 text-xs text-muted-foreground">
              {s.assignee_type === "role" ? `Any ${s.assignee_role}` : userName(s.assignee_user_id)}
              {s.signature && (
                <div className="mt-2 border-t border-border pt-2 text-foreground">
                  {s.signature.image && (
                    <img src={s.signature.image} alt="signature" className="h-10 mb-1 object-contain" data-testid={`sig-img-${i}`} />
                  )}
                  <div className="font-semibold" data-testid={`sig-name-${i}`}>{s.signature.typed_name}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">
                    {s.signature.role_label} · {(s.signature.signed_at || "").slice(0, 16).replace("T", " ")}
                  </div>
                  {s.signature.comment && <div className="italic mt-0.5">“{s.signature.comment}”</div>}
                </div>
              )}
              {canSign(s) && (
                <div className="mt-2 flex gap-2">
                  <Button data-testid={`sign-btn-${i}`} onClick={() => openSign(s)} size="sm" className="rounded-none bg-primary hover:bg-primary/90 h-7 text-xs">
                    <PenNib size={13} className="mr-1" />Sign
                  </Button>
                  <Button data-testid={`decline-btn-${i}`} onClick={() => setDeclineStep(s)} size="sm" variant="outline" className="rounded-none h-7 text-xs border-red-300 text-red-600 hover:bg-red-50">
                    Reject &amp; send back
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Route editor */}
        {editing && (
          <div className="space-y-3" data-testid="route-editor">
            {templates.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="overline text-muted-foreground">Template</span>
                <Select onValueChange={applyTemplate}>
                  <SelectTrigger data-testid="route-template-select" className="rounded-none h-8 flex-1 text-xs"><SelectValue placeholder="Load from a saved route…" /></SelectTrigger>
                  <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({(t.steps || []).length} steps)</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="overline text-muted-foreground">Order</span>
              <div className="inline-flex border border-border">
                <button data-testid="mode-sequential" onClick={() => setMode("sequential")} className={`px-2 py-1 text-xs font-semibold ${mode === "sequential" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>Sequential</button>
                <button data-testid="mode-parallel" onClick={() => setMode("parallel")} className={`px-2 py-1 text-xs font-semibold border-l border-border ${mode === "parallel" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>Parallel</button>
              </div>
            </div>
            <label className="flex items-start gap-2 text-xs cursor-pointer border border-border bg-secondary/30 p-2" data-testid="auto-restart-toggle-label">
              <Checkbox data-testid="auto-restart-toggle" checked={autoRestart} onCheckedChange={(v) => setAutoRestart(!!v)} className="rounded-none mt-0.5" />
              <span><span className="font-semibold">Auto-restart on resubmit</span> — when this {entityLabel} is rejected and the author resubmits it, automatically reset the signatures and re-notify the same approvers.</span>
            </label>
            {draftSteps.map((s, i) => (
              <div key={i} className="border border-border p-2 space-y-2" data-testid={`draft-step-${i}`}>
                <div className="flex items-center gap-2">
                  <div className="flex flex-col">
                    <button data-testid={`step-up-${i}`} onClick={() => moveStep(i, -1)} disabled={i === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30 leading-none">▲</button>
                    <button data-testid={`step-down-${i}`} onClick={() => moveStep(i, 1)} disabled={i === draftSteps.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30 leading-none">▼</button>
                  </div>
                  <Select value={s.assignee_type} onValueChange={(v) => setStep(i, { assignee_type: v })}>
                    <SelectTrigger data-testid={`draft-type-${i}`} className="rounded-none h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">Named person</SelectItem>
                      <SelectItem value="role">By role</SelectItem>
                    </SelectContent>
                  </Select>
                  {s.assignee_type === "role" ? (
                    <Select value={s.assignee_role} onValueChange={(v) => setStep(i, { assignee_role: v })}>
                      <SelectTrigger data-testid={`draft-role-${i}`} className="rounded-none h-8 flex-1 text-xs capitalize"><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Select value={s.assignee_user_id} onValueChange={(v) => setStep(i, { assignee_user_id: v })}>
                      <SelectTrigger data-testid={`draft-user-${i}`} className="rounded-none h-8 flex-1 text-xs"><SelectValue placeholder="Select person" /></SelectTrigger>
                      <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  <button onClick={() => removeStep(i)} className="text-muted-foreground hover:text-red-600"><Trash size={16} /></button>
                </div>
                <Input data-testid={`draft-label-${i}`} value={s.label} onChange={(e) => setStep(i, { label: e.target.value })}
                  placeholder="Signs as… e.g. CIP Senior Manager" className="rounded-none h-8 text-xs" />
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <Checkbox checked={s.required} onCheckedChange={(v) => setStep(i, { required: !!v })} className="rounded-none" />
                  Required signature
                </label>
              </div>
            ))}
            <Button data-testid="add-step-btn" onClick={addStep} variant="outline" size="sm" className="rounded-none"><Plus size={14} className="mr-1" />Add signer</Button>
            <div className="flex gap-2">
              <Button data-testid="save-route-btn" onClick={saveRoute} disabled={busy} className="rounded-none bg-primary hover:bg-primary/90 flex-1">Save approvers</Button>
              <Button onClick={() => setEditing(false)} variant="outline" className="rounded-none">Cancel</Button>
            </div>
          </div>
        )}

        {/* Manage actions */}
        {canManage && !editing && (
          <div className="flex flex-wrap gap-2 pt-1 items-center">
            {!locked && <Button data-testid="edit-approvers-btn" onClick={startEdit} variant="outline" size="sm" className="rounded-none">{steps.length ? "Edit approvers" : "Add approvers"}</Button>}
            {!locked && doc.workflow_status === "changes_requested" && steps.length > 0 && (
              <Button data-testid="restart-approval-btn" onClick={restart} disabled={busy} size="sm" className="rounded-none bg-primary hover:bg-primary/90"><ArrowsClockwise size={14} className="mr-1" />Restart approvals</Button>
            )}
            {locked && <Button data-testid="reopen-btn" onClick={reopen} disabled={busy} variant="outline" size="sm" className="rounded-none border-amber-400 text-amber-700 hover:bg-amber-50"><LockSimple size={14} className="mr-1" />Reopen to revise</Button>}
            {doc.auto_restart && steps.length > 0 && (
              <span data-testid="auto-restart-indicator" className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 border border-blue-200 bg-blue-50 px-2 py-0.5"><ArrowsClockwise size={11} />Auto-restart on resubmit</span>
            )}
          </div>
        )}

        {/* Approval history timeline */}
        {(doc.approval_history || []).length > 0 && (
          <div className="pt-1" data-testid="approval-history">
            <button data-testid="approval-history-toggle" onClick={() => setShowHistory((v) => !v)} className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground">
              <Clock size={13} />Approval history ({doc.approval_history.length}) {showHistory ? "▲" : "▼"}
            </button>
            {showHistory && (
              <div className="mt-2 border-l-2 border-border pl-3 space-y-2">
                {[...doc.approval_history].reverse().map((h, i) => {
                  const meta = {
                    signed: ["text-green-700", "signed"], approved: ["text-green-800", "fully approved"],
                    rejected: ["text-red-700", "rejected & sent back"], declined: ["text-red-700", "rejected & sent back"],
                    restarted: ["text-blue-700", "restarted approvals"], auto_restarted: ["text-blue-700", "auto-restarted"],
                    resubmitted: ["text-amber-700", "resubmitted"], reopened: ["text-amber-700", "reopened"],
                    route_set: ["text-muted-foreground", "set approvers"],
                  }[h.action] || ["text-foreground", h.action];
                  return (
                    <div key={i} data-testid={`history-entry-${i}`} className="text-xs">
                      <span className={`font-semibold ${meta[0]}`}>{h.by}</span>{" "}
                      <span className="text-muted-foreground">{meta[1]}{h.role_label ? ` (${h.role_label})` : ""}</span>
                      <span className="text-muted-foreground"> · {(h.at || "").slice(0, 16).replace("T", " ")}</span>
                      {(h.comment || h.note) && <div className="italic text-muted-foreground mt-0.5">“{h.comment || h.note}”</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sign dialog */}
      <Dialog open={!!signStep} onOpenChange={(o) => !o && setSignStep(null)}>
        <DialogContent className="rounded-none" data-testid="sign-dialog">
          <DialogHeader><DialogTitle>Sign as {signStep?.label || signStep?.assignee_role}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold">Your name (typed signature)</label>
              <Input data-testid="sign-typed-name" value={signForm.typed_name} onChange={(e) => setSignForm({ ...signForm, typed_name: e.target.value })} className="rounded-none mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold">Signature image (optional)</label>
              <div className="flex items-center gap-2 mt-1">
                <label className="inline-flex items-center gap-1 border border-border px-3 py-1.5 text-xs cursor-pointer hover:bg-secondary">
                  <UploadSimple size={14} />Upload
                  <input data-testid="sign-image-input" type="file" accept="image/*" className="hidden" onChange={(e) => onPickImage(e, (url) => setSignForm({ ...signForm, image: url }))} />
                </label>
                {signForm.image && <img src={signForm.image} alt="sig" className="h-8 object-contain border border-border" />}
                {signForm.image && <button onClick={() => setSignForm({ ...signForm, image: null })} className="text-xs text-red-600 underline">remove</button>}
              </div>
            </div>
            <Textarea data-testid="sign-comment" value={signForm.comment} onChange={(e) => setSignForm({ ...signForm, comment: e.target.value })} placeholder="Comment (optional)" className="rounded-none" />
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox data-testid="sign-save-profile" checked={signForm.save_to_profile} onCheckedChange={(v) => setSignForm({ ...signForm, save_to_profile: !!v })} className="rounded-none" />
              Save this as my reusable signature
            </label>
          </div>
          <DialogFooter>
            <Button onClick={() => setSignStep(null)} variant="outline" className="rounded-none">Cancel</Button>
            <Button data-testid="confirm-sign-btn" onClick={submitSign} disabled={busy} className="rounded-none bg-primary hover:bg-primary/90"><PenNib size={15} className="mr-1" />Apply signature</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline dialog */}
      <Dialog open={!!declineStep} onOpenChange={(o) => !o && setDeclineStep(null)}>
        <DialogContent className="rounded-none" data-testid="decline-dialog">
          <DialogHeader><DialogTitle>Reject &amp; send back</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">This returns the {entityLabel} to its author for revision and notifies them. Approvals restart after they resubmit.</p>
          <Textarea data-testid="decline-comment" value={declineComment} onChange={(e) => setDeclineComment(e.target.value)} placeholder="What needs to change? (shared with the author)" className="rounded-none" />
          <DialogFooter>
            <Button onClick={() => setDeclineStep(null)} variant="outline" className="rounded-none">Cancel</Button>
            <Button data-testid="confirm-decline-btn" onClick={submitDecline} disabled={busy} className="rounded-none bg-red-600 hover:bg-red-700">Reject &amp; send back</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
